import { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Loader2, X } from 'lucide-react';
import { productsApi, categoriesApi } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import type { Product, Category, ProductForm } from '@/types';
import clsx from 'clsx';

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const { user } = useAuthStore();

  const isAdmin = user?.role === 'admin';

  const fetchData = async () => {
    setIsLoading(true);
    const [productsRes, categoriesRes] = await Promise.all([
      productsApi.list({ search, category_id: selectedCategory || undefined }),
      categoriesApi.list(),
    ]);

    if (productsRes.success && productsRes.data) {
      setProducts(productsRes.data.items);
    }
    if (categoriesRes.success && categoriesRes.data) {
      setCategories(categoriesRes.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [search, selectedCategory]);

  const handleDelete = async (product: Product) => {
    if (!confirm(`「${product.name}」を削除しますか？`)) return;

    const response = await productsApi.delete(product.id);
    if (response.success) {
      fetchData();
    } else {
      alert(response.error || '削除に失敗しました');
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">商品マスタ</h1>
        {isAdmin && (
          <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            商品登録
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="商品名で検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
            className="input sm:w-48"
          >
            <option value="">すべてのカテゴリ</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            商品がありません
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>商品名</th>
                  <th>カテゴリ</th>
                  <th>単位</th>
                  <th className="text-right">現在庫</th>
                  <th className="text-right">最小在庫</th>
                  <th className="text-right">アラート日数</th>
                  {isAdmin && <th className="text-right">操作</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="font-medium">{product.name}</td>
                    <td>{product.category_name || '-'}</td>
                    <td>{product.unit}</td>
                    <td className="text-right">
                      <span className={clsx(
                        'font-medium',
                        product.total_stock !== undefined && 
                        product.total_stock < product.min_stock && 
                        'text-red-600'
                      )}>
                        {product.total_stock ?? 0}
                      </span>
                    </td>
                    <td className="text-right">{product.min_stock}</td>
                    <td className="text-right">{product.expiry_alert_days}日前</td>
                    {isAdmin && (
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-1 text-gray-500 hover:text-primary-600"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product)}
                            className="p-1 text-gray-500 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <ProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

interface ProductModalProps {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSave: () => void;
}

function ProductModal({ product, categories, onClose, onSave }: ProductModalProps) {
  const [form, setForm] = useState<ProductForm>({
    name: product?.name || '',
    category_id: product?.category_id || null,
    unit: product?.unit || 'kg',
    description: product?.description || '',
    min_stock: product?.min_stock || 0,
    expiry_alert_days: product?.expiry_alert_days || 7,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // 整数チェック
    if (!Number.isInteger(form.min_stock) || form.min_stock < 0) {
      setError('最小在庫は0以上の整数を入力してください');
      setIsLoading(false);
      return;
    }

    const response = product
      ? await productsApi.update(product.id, form)
      : await productsApi.create(form);

    if (response.success) {
      onSave();
    } else {
      setError(response.error || '保存に失敗しました');
    }
    setIsLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {product ? '商品編集' : '商品登録'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="label">商品名 *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input"
              placeholder="例: コシヒカリ"
            />
          </div>

          <div>
            <label className="label">カテゴリ</label>
            <select
              value={form.category_id || ''}
              onChange={(e) => setForm({ ...form, category_id: e.target.value ? parseInt(e.target.value) : null })}
              className="input"
            >
              <option value="">カテゴリなし</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">単位 *</label>
            <input
              type="text"
              required
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="input"
              placeholder="例: kg, 袋, 個"
            />
          </div>

          <div>
            <label className="label">説明</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input"
              rows={2}
              placeholder="商品の説明（任意）"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">最小在庫</label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.min_stock}
                onChange={(e) => setForm({ ...form, min_stock: parseInt(e.target.value) || 0 })}
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">整数で入力</p>
            </div>

            <div>
              <label className="label">期限アラート（日前）</label>
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                value={form.expiry_alert_days}
                onChange={(e) => setForm({ ...form, expiry_alert_days: parseInt(e.target.value) || 7 })}
                className="input"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              キャンセル
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                product ? '更新' : '登録'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
